import { __awaiter } from "tslib";
import { setDataLayer, trackGAEvent, initGTM, setGAConfig, confirmConsent as confirmGTMConsent, getGAConfig as getGAConfigFromGTM } from "../gtm/index.mjs";
import { getUserConsent, jsBridge, logGAEvent } from '@voghion/native-api';
import { Env } from '@voghion/fe-util';
import { isEnvProd, EVENT_EXCEPTION, AnalyticsChannel, EVENT_PAGE_VIEW } from "../utils/config.mjs";
import { initFirebase, getPerformance, getAnalytics, setUserId as _setUserId, setDefaultEventParameters as _setDefaultEventParameters, getGAClientId as getGAClientIdFromFirebase } from "../firebase/index.mjs";
import { trackEvent as trackEventFromFirebase } from "../firebase/index.mjs";
import { confirmMetaPixelConsent, initMetaPixel } from "../meta-pixel/index.mjs";
import { initMonitor } from "../monitor/index.mjs";
// meta pixel
export { trackFbq, trackCustomFbq } from "../meta-pixel/index.mjs";
// 网络监控
export { trackException, trackApiException, trackAxiosException } from "../monitor/index.mjs";
export { ErrorTypes } from "../monitor/config.mjs";
// ga
export { addStringValueToParams } from "../ga/util.mjs";
// common
export { AppEnv } from "../utils/config.mjs";
// gtm
export { trackGTMEvent } from "../gtm/index.mjs";
let currentOptions;
let currentInstance = null;
/**
 * 是否使用 js bridge
 * @returns
 */
const useNativeApi = () => {
    const env = Env.getInstance();
    return jsBridge.isMicroApp() || env.isApp();
};
/**
 * 埋点监控初始化，返回单例
 * @param options
 */
export const init = (options) => {
    const env = Env.getInstance();
    if (!env.isClient) {
        return null;
    }
    if (currentInstance) {
        return currentInstance;
    }
    currentOptions = options;
    jsBridge.init();
    let firebaseApp = null;
    let firebasePerformance = null;
    let analyticsChannel = AnalyticsChannel.Disable;
    const enable = 'enable' in options ? !!options.enable : true;
    const analyticsEnable = !!options.analytics;
    if (options.firebase && enable) {
        firebaseApp = initFirebase(options.firebase);
        if (firebaseApp) {
            firebasePerformance = getPerformance(firebaseApp);
        }
        if (analyticsEnable) {
            analyticsChannel = AnalyticsChannel.Firebase;
        }
    }
    if (options.gtm && enable) {
        if (analyticsEnable) {
            analyticsChannel = AnalyticsChannel.GTM;
        }
        initGTM(Object.assign(Object.assign({}, options.gtm), { needConsent: options.needConsent, userId: options.userId, deviceCode: options.deviceCode, analytics: analyticsEnable }));
    }
    if (options.metaPixel && enable) {
        initMetaPixel(Object.assign(Object.assign({}, options.metaPixel), { needConsent: options.needConsent }));
    }
    currentInstance = {
        firebaseApp,
        firebasePerformance,
        analyticsChannel,
        firebaseAnalytics: null,
        monitorClient: null
    };
    if (analyticsChannel === AnalyticsChannel.Firebase) {
        initFirebaseAnalytics();
    }
    if (options.defaultEventParameters &&
        analyticsChannel !== AnalyticsChannel.Disable) {
        setDefaultEventParameters(options.defaultEventParameters);
    }
    if (enable && useNativeApi() && options.needConsent) {
        getUserConsent({}).then((res) => {
            if (res.data) {
                confirmConsent(res.data);
            }
        });
    }
    let monitorClient = null;
    const monitor = 'monitor' in options
        ? typeof options.monitor === 'boolean' && options.monitor
            ? {}
            : options.monitor
        : {};
    if (monitor && enable) {
        monitorClient = initMonitor(Object.assign(Object.assign({}, monitor), { trackEvent }));
    }
    currentInstance.monitorClient = monitorClient;
    return currentInstance;
};
/**
 * 初始化
 */
function initFirebaseAnalytics() {
    if (currentInstance && !currentInstance.firebaseAnalytics) {
        const config = {
            send_page_view: !!currentOptions.autoTrackPageView,
            app_env: currentOptions.appEnv
        };
        if (!isEnvProd(currentOptions.appEnv)) {
            // 非正式环境设置内部标识，用于 ga 后台忽略流量
            config.traffic_type = 'internal';
        }
        if (currentOptions.deviceCode) {
            config.device_code = currentOptions.deviceCode;
        }
        currentInstance.firebaseAnalytics = getAnalytics(currentInstance.firebaseApp, {
            config
        });
        if (currentOptions.userId) {
            setUserId(currentOptions.userId);
        }
    }
}
/**
 * 设置埋点用户标识，主要用于异步获取用户标识再更新的场景，例如 pc 的无刷新登录等
 */
export const setUserId = (userId) => {
    if (!currentInstance) {
        return console.warn('need init track first!');
    }
    if (currentInstance.analyticsChannel === AnalyticsChannel.GTM) {
        setDataLayer({
            user_id: userId
        });
        setGAConfig({
            user_id: userId
        });
    }
    else if (currentInstance.analyticsChannel === AnalyticsChannel.Firebase) {
        initFirebaseAnalytics();
        return _setUserId(currentInstance.firebaseAnalytics, userId);
    }
};
/**
 * 设置公共参数
 */
export const setDefaultEventParameters = (params) => {
    if (!currentInstance) {
        return console.warn('need init track first!');
    }
    // 缓存公共参数
    currentOptions.defaultEventParameters = Object.assign(Object.assign({}, currentOptions.defaultEventParameters), params);
    if (currentInstance.analyticsChannel === AnalyticsChannel.GTM) {
        setGAConfig(params);
        setDataLayer(params);
    }
    else if (currentInstance.analyticsChannel === AnalyticsChannel.Firebase) {
        _setDefaultEventParameters(params);
    }
    else {
        return console.log(`set default parameters: ${JSON.stringify(params)}`);
    }
};
/**
 * 触发自定义事件
 * @returns
 */
export function trackEvent(event, params = {}) {
    const msg = `track event: ${event} params: ${JSON.stringify(params)}`;
    if (!currentInstance) {
        const env = Env.getInstance();
        if (env.isClient) {
            return console.warn('need init track first!');
        }
        else {
            return event === EVENT_EXCEPTION ? console.error(msg) : console.info(msg);
        }
    }
    if (currentInstance.analyticsChannel === AnalyticsChannel.Disable) {
        return console.log(msg);
    }
    if (useNativeApi() && event !== EVENT_EXCEPTION) {
        const nativeParams = Object.assign(Object.assign({}, currentOptions.defaultEventParameters), params);
        /**
         * 在微前端和 app 环境下，非 exception 事件(业务事件)都通过 jsBridge 上报
         */
        return logGAEvent({
            event,
            params: nativeParams
        });
    }
    if (currentInstance.analyticsChannel === AnalyticsChannel.GTM) {
        return trackGAEvent(event, params);
    }
    else if (currentInstance.analyticsChannel === AnalyticsChannel.Firebase) {
        initFirebaseAnalytics();
        return trackEventFromFirebase(currentInstance.firebaseAnalytics, event, params);
    }
    return false;
}
/**
 * 触发页面曝光
 */
export function trackPageView(params = {}) {
    return trackEvent(EVENT_PAGE_VIEW, params);
}
/**
 * 用户确认隐私协议
 * @param result
 */
export function confirmConsent(result) {
    confirmGTMConsent(result);
    confirmMetaPixelConsent(result);
}
const KEY_GA_CLIENT_ID = 'client_id';
/**
 * 获取 ga client id
 * @param key
 * @returns
 */
export function getGAClientId() {
    return __awaiter(this, void 0, void 0, function* () {
        if (!currentInstance) {
            console.warn('need init track first!');
            return '';
        }
        if (currentInstance.analyticsChannel === AnalyticsChannel.GTM) {
            return getGAConfigFromGTM(KEY_GA_CLIENT_ID);
        }
        else if (currentInstance.analyticsChannel === AnalyticsChannel.Firebase) {
            return getGAClientIdFromFirebase(currentInstance.firebaseAnalytics);
        }
        else {
            console.log(`not include ga`);
            return '';
        }
    });
}
